import { CoachActivityTypeEnum } from "@/modules/coach/coachActivityTypes"
import type { ProfessionalProfileDto } from "@/modules/coach/gateway/coachDto"
import { convertImageFromDto } from "@/modules/dtos/imageDto"
import type { HUImage } from "@modules/types/imageTypes"

export type CoachActivityEntry =
  | { kind: "predefined"; type: CoachActivityTypeEnum; id: string | null }
  | { kind: "other"; value: string; id: string | null }

type CoachSettingsState = {
  id: string
  role: string | null
  firstname: string | null
  lastname: string | null
  commercialName: string | null
  yearsOfExperience: number | null
  email: string | null
  phoneNumber: string | null
  phoneCountryCode: string | null
  description: string | null
  city: string | null
  region: string | null
  country: string | null
  profilePhoto: HUImage | null
  activities: CoachActivityEntry[]
  training: string | null
  galleryImages: HUImage[]
  additionalInformation: string[]
  archived: boolean
}

export class CoachSettingsEntity {
  private constructor(readonly state: CoachSettingsState) {}

  static fromDto(dto: ProfessionalProfileDto): CoachSettingsEntity {
    const sortedActivities = [...(dto.activityTypes ?? [])].sort((a, b) => a.orderIndex - b.orderIndex)

    const activities: CoachActivityEntry[] = sortedActivities.map((a) =>
      a.type === CoachActivityTypeEnum.OTHER
        ? { kind: "other", value: a.otherType ?? "", id: a.id }
        : { kind: "predefined", type: a.type, id: a.id },
    )

    const sortedInfo = [...(dto.additionalInformation ?? [])].sort((a, b) => a.orderIndex - b.orderIndex)

    return new CoachSettingsEntity({
      id: dto.id,
      role: dto.role,
      firstname: dto.firstname,
      lastname: dto.lastname,
      commercialName: dto.commercialName,
      yearsOfExperience: dto.yearsOfExperience,
      email: dto.email,
      phoneNumber: dto.phoneNumber?.number ?? null,
      phoneCountryCode: dto.phoneNumber?.countryCode ?? null,
      description: dto.description,
      city: dto.city,
      region: dto.region,
      country: dto.country,
      profilePhoto: dto.imageKey ? convertImageFromDto(dto.imageKey) : null,
      activities,
      training: dto.training,
      galleryImages: (dto.imageKeys ?? []).map(convertImageFromDto),
      additionalInformation: sortedInfo.map((i) => i.information),
      archived: dto.archived,
    })
  }

  getCoachFullName(): string {
    return `${this.state.firstname ?? ""} ${this.state.lastname ?? ""}`
  }

  getCoachDisplayName(): string {
    return this.state.commercialName ?? this.getCoachFullName()
  }
}
